import axios from 'axios';
import type { Assignee } from '../types/assignee';
import { EntityType } from './reminderService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

export { EntityType };

// Types
export interface Appointment {
  id: number;
  entityType: number; // 1 = Lead, 2 = Client
  entityId: number;
  entityName?: string;
  assignees: Assignee[]; // העובדים המשויכים (לפי סדר השיוך)
  appointmentType: number;
  appointmentTypeName: string;
  status: number;
  statusName: string;
  doctorName: string;
  appointmentDateTime?: string;
  phoneForScheduling?: string;
  address?: string;
  instructionsForClient?: string;

  // Meeting type and virtual link
  meetingType?: number;
  meetingTypeName?: string;
  virtualMeetingLink?: string;

  // Email invitation tracking
  emailInvitationSent: boolean;
  emailInvitationSentAt?: string;
  clientConfirmationStatus: number;
  clientConfirmationStatusName: string;
  clientConfirmedAt?: string;

  clientMedicalInfoId?: number;
  medicalConditionName?: string;
  createReminderBeforeAppointment: boolean;
  daysBeforeToRemind?: number;
  createReminderAfterAppointment: boolean;
  daysAfterToRemind?: number;
  completedAt?: string;
  completionNotes?: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateAppointmentDto {
  entityType: number; // 1 = Lead, 2 = Client
  entityId: number;
  assignedUserIds: number[]; // לפחות עובד אחד
  appointmentType: number;
  doctorName: string;
  appointmentDateTime?: string;
  phoneForScheduling?: string;
  address?: string;
  instructionsForClient?: string;

  // Meeting type and virtual link
  meetingType?: number;
  virtualMeetingLink?: string;

  clientMedicalInfoId?: number;
  createReminderBeforeAppointment: boolean;
  daysBeforeToRemind?: number;
  createReminderAfterAppointment: boolean;
  daysAfterToRemind?: number;
}

export interface UpdateAppointmentDto {
  appointmentType: number;
  status: number;
  doctorName: string;
  appointmentDateTime?: string;
  phoneForScheduling?: string;
  address?: string;
  instructionsForClient?: string;
  meetingType?: number;
  virtualMeetingLink?: string;
  clientMedicalInfoId?: number;
}

export interface CompleteAppointmentDto {
  completionNotes?: string;
}

// Enums
export enum AppointmentType {
  ExistingAppointment = 1,
  NeedToSchedule = 2
}

export enum AppointmentStatus {
  Scheduled = 1,
  Confirmed = 2,
  Completed = 3,
  Cancelled = 4,
  NeedScheduling = 5
}

export enum MeetingType {
  Frontal = 1,
  Virtual = 2
}

export enum ClientConfirmationStatus {
  Pending = 1,
  Confirmed = 2,
  Cancelled = 3
}

// API calls
export const appointmentsApi = {
  // Get appointments with filters
  getAppointments: async (filters?: {
    status?: number;
    fromDate?: string;
    toDate?: string;
  }): Promise<Appointment[]> => {
    const params = new URLSearchParams();
    if (filters?.status !== undefined) params.append('status', filters.status.toString());
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const response = await axios.get(`${API_BASE_URL}/appointments?${params.toString()}`);
    return response.data;
  },

  // Get upcoming appointments
  getUpcomingAppointments: async (daysAhead: number = 30): Promise<Appointment[]> => {
    const response = await axios.get(`${API_BASE_URL}/appointments/upcoming?daysAhead=${daysAhead}`);
    return response.data;
  },

  // Get client appointments
  getClientAppointments: async (clientId: number): Promise<Appointment[]> => {
    const response = await axios.get(`${API_BASE_URL}/appointments/client/${clientId}`);
    return response.data;
  },

  // Get lead appointments
  getLeadAppointments: async (leadId: number): Promise<Appointment[]> => {
    const response = await axios.get(`${API_BASE_URL}/appointments/lead/${leadId}`);
    return response.data;
  },

  // Get appointment by ID
  getAppointment: async (id: number): Promise<Appointment> => {
    const response = await axios.get(`${API_BASE_URL}/appointments/${id}`);
    return response.data;
  },

  // Create appointment
  createAppointment: async (appointment: CreateAppointmentDto): Promise<Appointment> => {
    const response = await axios.post(`${API_BASE_URL}/appointments`, appointment);
    return response.data;
  },

  // Update appointment
  updateAppointment: async (id: number, appointment: UpdateAppointmentDto): Promise<Appointment> => {
    const response = await axios.put(`${API_BASE_URL}/appointments/${id}`, appointment);
    return response.data;
  },

  // Complete appointment
  completeAppointment: async (id: number, data: CompleteAppointmentDto): Promise<Appointment> => {
    const response = await axios.post(`${API_BASE_URL}/appointments/${id}/complete`, data);
    return response.data;
  },

  // Delete appointment
  deleteAppointment: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/appointments/${id}`);
  },

  // Send invitation email
  sendInvitation: async (id: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/appointments/${id}/send-invitation`);
  },

  // Confirm appointment by token (public, no auth required)
  confirmAppointment: async (token: string): Promise<Appointment> => {
    const response = await axios.post(`${API_BASE_URL}/appointments/confirm/${token}`);
    return response.data;
  },

  // Cancel appointment by token (public, no auth required)
  cancelAppointment: async (token: string): Promise<Appointment> => {
    const response = await axios.post(`${API_BASE_URL}/appointments/cancel/${token}`);
    return response.data;
  },

  // Manual confirm appointment (by case manager)
  manualConfirmAppointment: async (id: number): Promise<Appointment> => {
    const response = await axios.post(`${API_BASE_URL}/appointments/${id}/manual-confirm`);
    return response.data;
  },

  // Manual cancel appointment (by case manager)
  manualCancelAppointment: async (id: number): Promise<Appointment> => {
    const response = await axios.post(`${API_BASE_URL}/appointments/${id}/manual-cancel`);
    return response.data;
  },

  // Replace the appointment's assignee set (at least one staff member)
  setAssignees: async (id: number, userIds: number[]): Promise<Appointment> => {
    const response = await axios.put(`${API_BASE_URL}/appointments/${id}/assignees`, { assignedUserIds: userIds });
    return response.data;
  }
};

// Helper functions
export const getAppointmentTypeLabel = (type: AppointmentType): string => {
  switch (type) {
    case AppointmentType.ExistingAppointment: return 'פגישה קיימת';
    case AppointmentType.NeedToSchedule: return 'צריך לתאם';
    default: return 'לא ידוע';
  }
};

export const getAppointmentTypeIcon = (type: AppointmentType): string => {
  switch (type) {
    case AppointmentType.ExistingAppointment: return '✅';
    case AppointmentType.NeedToSchedule: return '📞';
    default: return '❓';
  }
};

export const getAppointmentStatusLabel = (status: AppointmentStatus): string => {
  switch (status) {
    case AppointmentStatus.Scheduled: return 'מתוזמן';
    case AppointmentStatus.Confirmed: return 'אושר';
    case AppointmentStatus.Completed: return 'הושלם';
    case AppointmentStatus.Cancelled: return 'בוטל';
    case AppointmentStatus.NeedScheduling: return 'צריך תיאום';
    default: return 'לא ידוע';
  }
};

export const getAppointmentStatusColor = (status: AppointmentStatus): string => {
  switch (status) {
    case AppointmentStatus.Scheduled: return 'var(--primary-color)';
    case AppointmentStatus.Confirmed: return 'var(--success-color)';
    case AppointmentStatus.Completed: return 'var(--text-secondary)';
    case AppointmentStatus.Cancelled: return 'var(--error-color)';
    case AppointmentStatus.NeedScheduling: return 'var(--warning-color)';
    default: return 'var(--text-secondary)';
  }
};

export const getMeetingTypeLabel = (type: MeetingType): string => {
  switch (type) {
    case MeetingType.Frontal: return 'פרונטלי';
    case MeetingType.Virtual: return 'וירטואלי';
    default: return 'לא ידוע';
  }
};

export const getClientConfirmationStatusLabel = (status: ClientConfirmationStatus): string => {
  switch (status) {
    case ClientConfirmationStatus.Pending: return 'ממתין לאישור';
    case ClientConfirmationStatus.Confirmed: return 'אושר';
    case ClientConfirmationStatus.Cancelled: return 'בוטל';
    default: return 'לא ידוע';
  }
};

export const getClientConfirmationStatusColor = (status: ClientConfirmationStatus): string => {
  switch (status) {
    case ClientConfirmationStatus.Pending: return 'var(--warning-color)';
    case ClientConfirmationStatus.Confirmed: return 'var(--success-color)';
    case ClientConfirmationStatus.Cancelled: return 'var(--error-color)';
    default: return 'var(--text-secondary)';
  }
};

export const formatAppointmentDate = (dateString?: string): string => {
  if (!dateString) return 'לא נקבע';

  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Check if today
  if (date.toDateString() === today.toDateString()) {
    return `היום ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }

  // Check if tomorrow
  if (date.toDateString() === tomorrow.toDateString()) {
    return `מחר ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }

  // Otherwise show full date
  return date.toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';

export default appointmentsApi;
